import { Card } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  CheckCircle, Circle, AlertCircle, ArrowRight,
  User, Instagram, Youtube, Twitter, MapPin, FileText
} from 'lucide-react';

/**
 * ProfileCompletionCard - Shows profile completion progress and missing fields
 */
export function ProfileCompletionCard({ artistProfile, onScrollToField }) {
  // Define all required and recommended fields
  const fields = [
    {
      id: 'stage_name',
      label: 'Stage Name',
      icon: User,
      required: true,
      completed: !!artistProfile.stage_name?.trim(),
      section: 'profile'
    },
    {
      id: 'bio',
      label: 'Bio',
      icon: FileText,
      required: true,
      completed: !!artistProfile.bio?.trim(),
      section: 'profile'
    },
    {
      id: 'neighborhood',
      label: 'Neighborhood',
      icon: MapPin,
      required: true,
      completed: !!artistProfile.neighborhood,
      section: 'profile'
    },
    {
      id: 'instagram',
      label: 'Instagram',
      icon: Instagram,
      required: true,
      completed: !!artistProfile.socials?.instagram?.trim(),
      section: 'socials'
    },
    {
      id: 'youtube',
      label: 'YouTube',
      icon: Youtube,
      required: true,
      completed: !!artistProfile.socials?.youtube?.trim(),
      section: 'socials'
    },
    {
      id: 'additional_social',
      label: 'Additional Social',
      icon: Twitter,
      required: true,
      completed: !!(
        artistProfile.socials?.twitter?.trim() ||
        artistProfile.socials?.tiktok?.trim() ||
        artistProfile.socials?.soundcloud?.trim() ||
        artistProfile.socials?.website?.trim() ||
        artistProfile.socials?.facebook?.trim()
      ),
      section: 'socials',
      hint: 'Twitter, TikTok, or Website'
    }
  ];

  const requiredFields = fields.filter(f => f.required);
  const completedCount = requiredFields.filter(f => f.completed).length;
  const totalRequired = requiredFields.length;
  const percentage = Math.round((completedCount / totalRequired) * 100);
  const isComplete = percentage === 100;

  // Group missing fields by section
  const missingFields = fields.filter(f => f.required && !f.completed);

  if (isComplete) {
    return (
      <Card className="glass-card border-green-500/30 p-4 mb-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10" data-testid="profile-complete-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-green-400">Profile Complete!</p>
            <p className="text-sm text-muted-foreground">Your profile is ready for fans to discover.</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-amber-500/30 p-5 mb-6" data-testid="profile-completion-card">
      {/* Header with percentage */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <p className="font-heading font-bold">Complete Your Profile</p>
            <p className="text-sm text-muted-foreground">Required to upload music and appear on charts</p>
          </div>
        </div>
        <div className="text-right">
          <span className="text-2xl font-heading font-bold text-amber-500">{percentage}%</span>
          <p className="text-xs text-muted-foreground">{completedCount}/{totalRequired} fields</p>
        </div>
      </div>

      {/* Progress bar */}
      <Progress value={percentage} className="h-2 mb-4" />

      {/* Checklist of required fields */}
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-2">Required Fields</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {requiredFields.map((field) => {
            const Icon = field.icon;
            return (
              <div 
                key={field.id}
                className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                  field.completed 
                    ? 'bg-green-500/10 text-green-400' 
                    : 'bg-muted/30 text-muted-foreground hover:bg-muted/50 cursor-pointer'
                }`}
                onClick={() => !field.completed && onScrollToField?.(field.id)}
              >
                {field.completed ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <Circle className="w-4 h-4 flex-shrink-0" />
                )}
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{field.label}</span>
                {field.hint && !field.completed && (
                  <span className="text-xs text-muted-foreground ml-auto">({field.hint})</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Action prompt */}
      {missingFields.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            <span className="text-amber-500 font-medium">Missing: </span>
            {missingFields.map(f => f.label).join(', ')}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
            onClick={() => onScrollToField?.(missingFields[0].id)}
          >
            Fill in missing fields
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </Card>
  );
}
